import type { StorefrontProduct } from './dropify'

const CORRUPTED_SIZE_SLUGS = new Set([
  'longtorso-premium-lt-sb003-20260521-025',
  'bra-wa-003-breitex-6566',
  'bra-wa-008-breitex-8222',
  'bra-wa-024-breitex-816',
  'bra-wa-031-breitex-1237',
  'bra-wa-044-guojisan-gjb340',
  'bra-wa-067-breitex-2026',
  'bra-wa-078-bh-5638',
  'bra-wa-081-art-811-breitex',
  'bra-wa-121-oreo-888',
  'bra-wa-128-oreo-232',
  'bra-wa-131-breitex-6112',
  'bra-wa-149-breitex-6113',
  'bra-wa-155-oreo-x9',
  'bra-wa-160-bh-1734',
  'bra-wa-161-oreo',
  'bra-wa-163-oreo-305',
  'baju-anak-002-dress-ruffle-fairy',
  'baju-anak-012-singlet-alexa',
])

const DISPLAY_NAME_BY_SLUG: Record<string, string> = {
  'handuk-whatsapp-022-20260522-022': 'Handuk Sarang Lebah WA-022',
  'handuk-whatsapp-063-20260522-063': 'Handuk WA-063',
  'baju-anak-038-set-kirana-size-chart': 'Set Kirana',
  'baju-anak-051-singlet-justin-detail': 'Singlet Justin',
  'baju-anak-046-celine-size-chart': 'Celine',
  'baju-anak-054-t-shirt-axel-detail': 'T-Shirt Axel',
  'baju-anak-053-singlet-kimberly-detail': 'Singlet Kimberly',
}

const DESCRIPTION_BY_SLUG: Record<string, string> = {
  'sarung-tangan-matahari-putih-dot-hitam-all-size':
    'Sarung Tangan Matahari varian all size berwarna putih dengan bintik hitam pada telapak dan jari. Satu kemasan berisi 12 pasang. Konfirmasikan jumlah per bal dan standar keselamatan yang diwajibkan sebelum memesan.',
  'sarung-tangan-matahari-biru-muda-dot-hitam':
    'Sarung Tangan Matahari varian all size berwarna biru muda dengan bintik hitam pada telapak dan jari. Satu kemasan berisi 12 pasang. Konfirmasikan jumlah per bal dan standar keselamatan yang diwajibkan sebelum memesan.',
  'sarung-tangan-matahari-merah-bendera-dot-hitam':
    'Sarung Tangan Matahari varian all size berwarna merah bendera dengan bintik hitam pada telapak dan jari. Satu kemasan berisi 12 pasang. Konfirmasikan jumlah per bal dan standar keselamatan yang diwajibkan sebelum memesan.',
  'sarung-tangan-matahari-navy-dot-all-size':
    'Sarung Tangan Matahari varian all size berwarna navy dengan bintik pada telapak dan jari. Satu kemasan berisi 12 pasang. Konfirmasikan jumlah per bal dan standar keselamatan yang diwajibkan sebelum memesan.',
  'sarung-tangan-bulldozer':
    'Sarung Tangan Bulldozer berwarna abu-abu, memakai rajutan tanpa bintik, dan memiliki pergelangan elastis. Satu kemasan berisi 12 pasang. Jika produk akan dipakai pada pekerjaan dengan risiko sayatan, panas, bahan kimia, atau listrik, kirim standar perlindungan yang diwajibkan agar admin dapat memeriksa pilihan yang sesuai.',
  'handuk-mandi-katun-cermai-sarang-lebah':
    'Handuk mandi katun merek Sarang Lebah dengan motif Cermai. Konfirmasikan ukuran, berat, warna, dan isi kemasan sebelum memesan.',
  'perlengkapan-bayi-set-007':
    'Perlengkapan Bayi Set 007. Konfirmasikan isi set, bahan, ukuran, dan warna sebelum memesan.',
  'perlengkapan-bayi-set-011':
    'Perlengkapan Bayi Set 011. Konfirmasikan isi set, bahan, ukuran, dan warna sebelum memesan.',
  'perlengkapan-bayi-set-013':
    'Perlengkapan Bayi Set 013. Konfirmasikan isi set, bahan, ukuran, dan warna sebelum memesan.',
  'perlengkapan-bayi-set-020':
    'Perlengkapan Bayi Set 020. Konfirmasikan isi set, bahan, ukuran, dan warna sebelum memesan.',
  'celana-dalam-wanita-tally-cd-8903':
    'Celana Dalam Wanita Tally CD 8903 adalah model knit panties berbahan lentur dengan potongan free size. Pilihan warna perlu dikonfirmasi sebelum memesan.',
  'kaos-kaki-bola-strip-dewasa':
    'Kaos kaki bola strip untuk ukuran dewasa. Satu lusin berisi 12 pasang. Konfirmasikan ukuran, warna, dan motif yang tersedia sebelum memesan.',
}

function plainText(value: string | null | undefined) {
  return String(value ?? '')
    .replace(/<\/(?:p|li|h[1-6]|div)>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function isGenericDescription(description: string, name: string) {
  const normalized = description.toLowerCase()
  return !description
    || normalized === name.toLowerCase()
    || normalized === 'piyama & oneset'
    || /desain sederhana.*nyaman/i.test(description)
    || /produk katalog kaos kaki dropify/i.test(description)
    || /untuk stok katalog.*(?:live shop|live shopping|social commerce)/i.test(description)
}

function removeCorruptedSize(value: string) {
  return value
    .replace(/<li>\s*Ukuran:\s*.*?<\/li>/gi, '')
    .replace(/<p>\s*Ukuran:\s*.*?<\/p>/gi, '')
    .replace(/(?:^|\s)Ukuran:\s*[^.]+\.?/gi, ' ')
}

function fallbackDescription(product: StorefrontProduct, name: string) {
  const category = (product.categories ?? []).map((item) => item.name).filter(Boolean).join(', ')
  const categorySentence = category ? ` Produk ini termasuk kategori ${category}.` : ''
  return `${name}.${categorySentence} Detail bahan, ukuran, warna, isi kemasan, dan satuan pembelian perlu dikonfirmasi sebelum memesan.`
}

/**
 * Repairs known catalog-copy defects before a product reaches any public route.
 * Missing facts stay explicit instead of being filled with guesses from a name
 * or photograph. The source API record remains unchanged.
 */
export function cleanStorefrontProductCopy(product: StorefrontProduct): StorefrontProduct {
  const name = DISPLAY_NAME_BY_SLUG[product.slug] ?? product.name
  const descriptionOverrideApplied = Boolean(DESCRIPTION_BY_SLUG[product.slug])
  let description = DESCRIPTION_BY_SLUG[product.slug] ?? product.description ?? ''

  if (CORRUPTED_SIZE_SLUGS.has(product.slug)) {
    description = plainText(removeCorruptedSize(description))
    description = `${description ? `${description} ` : ''}Ukuran produk perlu dikonfirmasi melalui label atau size chart yang sesuai sebelum memesan.`
  }

  const descriptionText = plainText(description)
  if (isGenericDescription(descriptionText, product.name) || isGenericDescription(descriptionText, name)) {
    description = fallbackDescription(product, name)
  }

  const invalidSizeMetadata = CORRUPTED_SIZE_SLUGS.has(product.slug)
  const renameApplied = name !== product.name
  const physicalPriceInMetadata = /\b(?:harga\s+grosir\s+mulai|Rp\s*[\d.]+)/i.test(product.meta_description ?? '')
  const replaceName = (value: string | null) => value && renameApplied
    ? value.replaceAll(product.name, name).replace(/\bSize Chart\b|\bDetail\b/gi, '').replace(/\s+/g, ' ').trim()
    : value

  return {
    ...product,
    name,
    description,
    meta_title: invalidSizeMetadata ? `Grosir ${name} | Dropify` : replaceName(product.meta_title),
    meta_description: invalidSizeMetadata || descriptionOverrideApplied || physicalPriceInMetadata
      ? `${name}. Hubungi admin untuk meminta detail produk, informasi stok, dan quotation.`
      : replaceName(product.meta_description),
    tags: (product.tags ?? []).filter((tag) => !/watermark-test/i.test(`${tag.name} ${tag.slug}`)),
    photos: (product.photos ?? []).map((photo) => ({
      ...photo,
      alt_text: replaceName(photo.alt_text),
      caption: replaceName(photo.caption),
    })),
    variants: product.variants ?? [],
  }
}

export function cleanStorefrontProductList<T extends { items: StorefrontProduct[] }>(response: T): T {
  return { ...response, items: response.items.map(cleanStorefrontProductCopy) }
}
